import React, { useContext, useEffect, useState, useRef } from 'react';
import { ShopContext } from '../../../contexts/ShopContext';
import ProductService from '../../../services/ProductService';
import styles from './styles.module.css';
import { Skeleton } from '@mui/material';
import PageHeader from '../../utils/PageHeader/PageHeader';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { Grid, Dialog, DialogTitle } from '@mui/material';
import ViewHelper from '../../../helpers/ViewHelper';
import { ROUTES, STOCK_STATUS, SHOP_MODE, PRODUCT_TYPES } from '../../../const';
import CustomButton from '../../utils/CustomerComponents/CustomButton';
import ShopHelper from '../../../helpers/ShopHelper';
import ProductQtyUpdateSec from '../../utils/ProductView/ProductQtyUpdateSec';
import { useHistory } from 'react-router-dom';
import { CartContext } from '../../../contexts/CartContext';
import OptionPopup from '../../utils/ProductView/OptionPopup';

export default function ProductDetailsPage(props) {
  const { shop } = useContext(ShopContext);
  const { cart, addItemToCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const history = useHistory();

  const { shopId, productId } = props?.match?.params;

  const [openImagePopup, setOpenImagePopup] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      if (images.length > 1) {
        if (isLeftSwipe) {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        } else {
          setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        }
      }
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const images = product?.images?.length ? product.images : (product?.image ? [product.image] : []);
  const currentImage = images[currentImageIndex] || '';

  const handleOpenImage = () => setOpenImagePopup(true);
  const handleCloseImage = () => setOpenImagePopup(false);

  useEffect(() => {
    if (!shop || !productId) return;

    async function loadProduct() {
      const product = await ProductService.getProduct(shop, productId);

      const optionsArray =
        product.product_options?.length > 0
          ? product.product_options
          : product.config?.product_options?.length > 0
          ? product.config.product_options
          : [];

      let allImages = [];
      if (product.images && product.images.length > 0) {
        allImages = product.images.map(img => typeof img === 'object' ? img.url || img.image : img).filter(Boolean);
      } else {
        if (product.image) allImages.push(product.image);
        if (product.image_2 && !product.image_2.includes('default.jpg')) allImages.push(product.image_2);
        if (product.image_3 && !product.image_3.includes('default.jpg')) allImages.push(product.image_3);
        if (product.image_4 && !product.image_4.includes('default.jpg')) allImages.push(product.image_4);
      }

      const mappedProduct = {
        ...product,
        images: allImages,
        options: {
          product_options: optionsArray,
        },
        selectedOption: null,
        originalName: product.name,
      };
      setProduct(mappedProduct);
    }
    loadProduct();
  }, [shop, productId]);

  if (shop.shopId !== Number(shopId)) {
    return (
      <div>
        <h3>Invalid shop data! Please refresh the page</h3>
        <div>
          Context shop ID: {shop.shopId}, URL shop ID: {shopId}
        </div>
      </div>
    );
  }

  const handleViewCart = () => {
    history.push(ROUTES.CART.replace(':shopId', shopId));
  };

  const stockStatus =
    product && product.type === PRODUCT_TYPES.SERVICE
      ? null
      : product && product?.stock <= 0
      ? STOCK_STATUS.sold_out
      : (product && product.stock - product.stock_on_hold) <= 0
      ? STOCK_STATUS.on_hold
      : null;

  const isViewOnly = shop?.mode === SHOP_MODE.VIEW_ONLY;

  const handleOptionSelect = (option) => {
    if (!selectedProduct) return;

    const updatedProduct = {
      ...selectedProduct,
      selectedOption: option,
      name: `${selectedProduct.originalName} ${option.label}`,

      price: option.price,
      quantity_ratio: option.quantity_ratio,
    };

    setProduct(updatedProduct);
    addItemToCart(updatedProduct);

    setPopupOpen(false);
    setSelectedProduct(null);
  };

  return (
    <>
      <PageHeader title="Details" />
      <div className={styles.container}>
        {!product ? (
          <>
            <Skeleton variant="rectangular" width="100%" className={styles.skeleton} height={200} />
            <Skeleton height={50} />
            <Skeleton width="60%" height={50} />
            <Skeleton variant="rectangular" width="100%" className={styles.skeleton} height={120} />
          </>
        ) : (
          <Grid container>
            <Grid 
              item 
              xs={12} 
              className={styles.displayFlex}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={currentImage}
                alt={product.name}
                className={styles.productImg}
                style={{ cursor: 'pointer', objectFit: 'contain' }}
                onClick={handleOpenImage}
              />
            </Grid>
            {images.length > 1 && (
              <Grid item xs={12} display="flex" justifyContent="center" gap={1} pt={2}>
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    style={{
                      width: 60,
                      height: 60,
                      border: currentImageIndex === idx ? '2px solid #D32F2F' : '1px solid #eee',
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      padding: 2,
                    }}
                  >
                    <img src={img} alt={`thumb-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                ))}
              </Grid>
            )}
            <Grid item container xs={12} pt={1}>
              <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                <span className={styles.title}>{ViewHelper.toTitleCase(product.name)}</span>{' '}
                {stockStatus ? (
                  <span style={{ color: stockStatus.color }} className={styles.text}>
                    {stockStatus.label}
                  </span>
                ) : (
                  !isViewOnly && (
                    <ProductQtyUpdateSec
                      product={product}
                      handleOpenOptions={(product) => {
                        setSelectedProduct(product);
                        setPopupOpen(true);
                      }}
                    />
                  )
                )}
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
                <span className={styles.hilight}>
                  {ShopHelper.getShopCurrency(shop)} {(() => {
                    const cartProducts = Object.values(cart?.products || {});
                    const productQty = cartProducts.reduce((sum, item) => {
                      const sameId = product && item.product.id === product.id;
                      if (!sameId) return sum;
                      const itemOption = item.product.selectedOption?.label || null;
                      const productOption = product?.selectedOption?.label || null;
                      if (productOption) {
                        return itemOption === productOption ? sum + item.quantity : sum;
                      }
                      return sum + item.quantity;
                    }, 0);
                    const qtyToMultiply = productQty > 0 ? productQty : 1;
                    const total = product?.price ? (product.price * qtyToMultiply) : 0;
                    return Number.isInteger(total) ? total : Number(total.toFixed(2));
                  })()}
                </span>
              </Grid>
            </Grid>
            <Grid item xs={12} pt={2}>
              <div className={styles.divider}></div>
            </Grid>
            <Grid item xs={12} pt={2}>
              <span className={styles.header}>Description</span>
            </Grid>
            <Grid item xs={12} pt={2}>
              <span 
                className={styles.description} 
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </Grid>
            {!isViewOnly && (
              <Grid item xs={12} className={styles.btnContainer}>
                <CustomButton fullWidth onClick={handleViewCart}>
                  View Cart
                </CustomButton>
              </Grid>
            )}
          </Grid>
        )}
      </div>

      <Dialog
        open={openImagePopup}
        onClose={handleCloseImage}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          style: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        }}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: 0,
            maxWidth: '600px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            pb: 0,
          }}
        >
          <IconButton aria-label="close" onClick={handleCloseImage} sx={{ color: '#444' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <div 
          style={{ paddingLeft: 25, paddingRight: 25, paddingBottom: 25, textAlign: 'center' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={currentImage}
            alt={product?.name}
            style={{
              width: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              display: 'block',
            }}
          />
          {images.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  style={{
                    width: 60,
                    height: 60,
                    border: currentImageIndex === idx ? '2px solid #D32F2F' : '1px solid #eee',
                    borderRadius: 8,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    padding: 2,
                  }}
                >
                  <img src={img} alt={`thumb-popup-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>

      {selectedProduct && selectedProduct.options?.product_options?.length > 0 && (
        <OptionPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          product={selectedProduct}
          onSelectOption={handleOptionSelect}
        />
      )}
    </>
  );
}
